import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { DeskIconSpec, Project } from './types'

import { useRef } from 'react'

import { useDesk95Theme } from './theme'

export const ICON_TILE_W = 76
export const ICON_TILE_H = 88

type DesktopIconProps = {
    spec: DeskIconSpec
    project?: Project
    x: number
    y: number
    z: number
    selected: boolean
    tapToOpen: boolean
    onSelect: () => void
    onOpen: () => void
    onPointerDown: (event: ReactPointerEvent) => void
}

export const DesktopIcon = ({
    spec,
    project,
    x,
    y,
    z,
    selected,
    tapToOpen,
    onSelect,
    onOpen,
    onPointerDown
}: DesktopIconProps): ReactNode => {
    const palette = useDesk95Theme()
    const downAt = useRef<{ x: number; y: number } | null>(null)

    return (
        <div
            className='desk95-handle absolute flex flex-col items-center gap-[3px] select-none'
            style={{ left: x, top: y, width: ICON_TILE_W, zIndex: z }}
            onPointerDown={event => {
                downAt.current = { x: event.clientX, y: event.clientY }
                onSelect()
                onPointerDown(event)
            }}
            onPointerUp={event => {
                if (!tapToOpen || !downAt.current) return

                const moved = Math.hypot(event.clientX - downAt.current.x, event.clientY - downAt.current.y)

                downAt.current = null

                if (moved < 6) onOpen()
            }}
            onDoubleClick={onOpen}
            role='button'
            tabIndex={0}
            aria-label={spec.label}
            onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onOpen()
                }
            }}
        >
            {project ? (
                <div
                    className='bg-white p-[3px]'
                    style={{
                        border: '3px solid #000000',
                        boxShadow: `5px 5px 0 0 ${palette.shadow}`
                    }}
                >
                    <img
                        src={project.image}
                        alt={project.alt}
                        width={40}
                        height={40}
                        className='block size-[40px] object-cover'
                        draggable={false}
                    />
                </div>
            ) : (
                <img
                    src={spec.icon}
                    alt=''
                    width={32}
                    height={32}
                    className='desk95-pixel block size-[32px]'
                    draggable={false}
                />
            )}

            <span
                className='max-w-full px-[2px] text-center text-[11px] leading-[13px] break-words'
                style={{
                    color: '#ffffff',
                    background: selected ? palette.selection : 'transparent',
                    outline: selected ? '1px dotted #ffffff' : 'none',
                    textShadow: selected ? 'none' : '1px 1px 0 rgba(0, 0, 0, 0.65)'
                }}
            >
                {spec.label}
            </span>
        </div>
    )
}
